import React, {forwardRef, useState, useEffect, useImperativeHandle} from 'react';
import {observer} from 'mobx-react';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import cx from 'classnames';
import styles from '../styles/PersonalizationCenter.module.scss';
import {formsStore} from '../../../_legacy/Form/stores/FormsStore';
import {getIndexedData} from '../service/indexDBPersonalizarionCenterService';
import {db} from '../../../services/indexDB/indexeddbPersonalizationCenter';
import {SnoozedSuggestion, SuggestionType} from '../../Personalization/types/personalizationCenter';
import {HOMEPAGE_REFERENCE} from '../../Personalization/consts';
import {getLanguage} from '../../../_shared/GWTBridge/invoke/common';
import {CenterDropdown} from './CenterDropdown';
import {
    showHomepageSuggestion,
    showActionsSuggestions,
    showSnoozedSearchesSuggestion
} from '../../Personalization/actions';
import {invokeSnoozedSubFormsSuggestion} from '../../../_shared/GWTBridge/invoke/netForm';
import {sendMonitor} from '../../../utils/monitoring';
import {personalizationSearchesDialogRef} from './PersonalizationSearchesDialog.root';
import {personalizationDialogRef} from './PersonalizationDialog.root';

export const PersonalizationCenter = observer(
    forwardRef((children, ref) => {
        const [centerData, setCenterData] = useState<SnoozedSuggestion[]>([]);
        const [open, setOpen] = useState<boolean>(false);
        const [shouldReloadData, setShouldReload] = useState<boolean>(false);
        const [iconMonitoringText, setMonitorText] = useState<string>(null);

        useImperativeHandle(ref, () => {
            return {
                reloadData
            };
        });

        const reloadData = () => {
            setShouldReload(true);
        };

        useEffect(() => {
            const reference = formsStore.currentForm?.name || HOMEPAGE_REFERENCE;
            getIndexedData(db).then(res => {
                const relevantSuggestions: SnoozedSuggestion[] = [];
                const data = res && res.data;
                if (data) {
                    data.map(s => {
                        switch (s.type) {
                            case SuggestionType.Homepage:
                            case SuggestionType.Subforms:
                            case SuggestionType.Actions:
                                if (s.reference === reference) {
                                    relevantSuggestions.push(s);
                                }
                                break;
                            case SuggestionType.Searches:
                                if (s.reference === reference && s.lang === getLanguage()) {
                                    if (s.search?.filters) {
                                        formsStore.searchesStore.setRecommendedSearch(s.search);
                                    }
                                    relevantSuggestions.push(s);
                                }
                                break;
                        }
                    });
                }
                setCenterData(relevantSuggestions);
                if (shouldReloadData) {
                    setShouldReload(false);
                }
            });
            formsStore.currentForm?.name
                ? setMonitorText('PersonalizationIconForm')
                : setMonitorText('PersonalizationIconHP');
        }, [formsStore.currentForm, shouldReloadData]);

        const closeOpenDialog = () => {
            personalizationSearchesDialogRef?.current?.handleClose?.();
            personalizationDialogRef?.current?.handleClose?.();
        };

        const renderSnoozedSuggestions = (suggestionType: string) => {
            setOpen(false);
            closeOpenDialog();
            const shouldMonitor = centerData.length > 1;
            switch (suggestionType) {
                case SuggestionType.Homepage:
                    return showHomepageSuggestion(false, true);
                case SuggestionType.Subforms:
                    shouldMonitor && sendMonitor('PersonalizationIconSLF');
                    return invokeSnoozedSubFormsSuggestion();
                case SuggestionType.Actions:
                    if (formsStore.currentForm) {
                        shouldMonitor && sendMonitor('PersonalizationIconAC');
                        return showActionsSuggestions(
                            formsStore.currentForm.name,
                            formsStore.currentForm.directActivations,
                            true
                        );
                    }
                    return;
                case SuggestionType.Searches:
                    const searchSuggestion = centerData.find(
                        s => s.type === SuggestionType.Searches
                    );
                    shouldMonitor && sendMonitor('PersonalizationIconSS');

                    return showSnoozedSearchesSuggestion(
                        formsStore.currentForm,
                        searchSuggestion.search
                    );
            }
        };

        const isDropdownEnabled = open && centerData.length > 1;

        return (
            <div
                className={cx(styles.iconContainer, {
                    [styles.active]: isDropdownEnabled
                })}
            >
                {centerData.length > 0 && (
                    <Icon
                        componentClasses={cx(styles.centerIcon, {
                            [styles.iconDisabled]: isDropdownEnabled
                        })}
                        icon={PriorityIcon.bulb}
                        iconColor={isDropdownEnabled ? 'primary-500' : 'white'}
                        style={{fontSize: '22px', margin: '8px 0', textAlign: 'center'}}
                        onClick={e => {
                            sendMonitor(iconMonitoringText);
                            if (centerData.length > 1) {
                                e.stopPropagation();
                                setOpen(true);
                            } else {
                                renderSnoozedSuggestions(centerData[0].type);
                            }
                        }}
                    />
                )}
                {isDropdownEnabled && (
                    <CenterDropdown
                        items={centerData}
                        renderSnoozedSuggestions={renderSnoozedSuggestions}
                        closeDropdown={() => {
                            sendMonitor('PersonalizationIconX');
                            setOpen(false);
                        }}
                    />
                )}
            </div>
        );
    })
);
